// ===== BEGIN FILE: src/components/report-v2/ReportGenerator.tsx =====
/**
 * Main report generator orchestrator
 * Handles report configuration, data collection, and rendering
 */

import React, { useState, useRef, useCallback } from 'react';
import { Box, Button, Card, Typography, Modal, ModalDialog, ModalClose, Sheet, CircularProgress } from '@mui/joy';
import { useReactToPrint } from 'react-to-print';
import { ReportCover } from './ReportCover';
import { ReportIntroPage } from './ReportIntroPage';
import { ReportIndexPage } from './ReportIndexPage';
import { ReportClosingPage } from './ReportClosingPage';
import { ReportFAQPage } from './ReportFAQPage';
import { BirthDetailsSection } from './sections/BirthDetailsSection';
import { ChartSection } from './sections/ChartSection';
import { HouseScoresSection } from './sections/HouseScoresSection';
import { HouseCombinedSection } from './sections/HouseCombinedSection';
import { PlanetDetailsSection } from './sections/PlanetDetailsSection';
import { YogasSection } from './sections/YogasSection';
import { DashaTimelineSection } from './sections/DashaTimelineSection';
import { LifeAreasSection } from './sections/LifeAreasSection';
import { OpportunityWindowsSection } from './sections/OpportunityWindowsSection';
import { PlanetFactorsSection } from './sections/PlanetFactorsSection';
import { HouseBalanceSection } from './sections/HouseBalanceSection';
import { YearForecastSection } from './sections/YearForecastSection';
import type { ReportData, ReportConfig } from './types';

interface ReportGeneratorProps {
  open: boolean;
  onClose: () => void;
  reportData: ReportData;
  config?: Partial<ReportConfig>;
  astroData?: any;
  vargaMap?: any;
  /** Called when user clicks "View in App" from a section — receives the target tab name */
  onNavigate?: (tab: string) => void;
  /** Admin-only AI analysis props */
  isAdmin?: boolean;
  onAddSwotAnalysis?: () => void;
  onAddDetailedAnalysis?: () => void;
  onAddCurrentPeriodAnalysis?: () => void;
  swotContent?: string;
  swotLoading?: boolean;
  detailedContent?: string;
  detailedLoading?: boolean;
  currentPeriodContent?: string;
  currentPeriodLoading?: boolean;
  onAddYearForecast?: () => void;
  yearForecastContent?: string;
  yearForecastLoading?: boolean;
  /** localStorage cache key — when provided, admin sees a Clear Cache button */
  reportAiCacheKey?: string | null;
}

export const ReportGenerator: React.FC<ReportGeneratorProps> = ({
  open,
  onClose,
  reportData,
  config: customConfig,
  astroData,
  vargaMap,
  onNavigate,
  isAdmin,
  onAddSwotAnalysis,
  onAddDetailedAnalysis,
  onAddCurrentPeriodAnalysis,
  swotContent,
  swotLoading,
  detailedContent,
  detailedLoading,
  currentPeriodContent,
  currentPeriodLoading,
  onAddYearForecast,
  yearForecastContent,
  yearForecastLoading,
  reportAiCacheKey,
}) => {
  const [cacheCleared, setCacheCleared] = React.useState(false);

  const handleClearAiCache = () => {
    if (!reportAiCacheKey) return;
    ['swot', 'detailed', 'current_period', 'year_forecast'].forEach(type =>
      localStorage.removeItem(`${reportAiCacheKey}_${type}`)
    );
    setCacheCleared(true);
    setTimeout(() => setCacheCleared(false), 2500);
  };
  const reportRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleSectionClick = useCallback((sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const container = scrollContainerRef.current;
    if (container) {
      const containerTop = container.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      container.scrollBy({ top: elTop - containerTop - 16, behavior: 'smooth' });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExportingHTML, setIsExportingHTML] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);

  /** Convert a single img element's src to a base64 data URI.
   *  Relative paths are resolved against window.location.origin so they work
   *  during local dev (localhost:3000) and on production (shreemastro.com). */
  const toDataUri = (src: string): Promise<string> =>
    new Promise(resolve => {
      // Already a data URI — nothing to do
      if (src.startsWith('data:')) { resolve(src); return; }
      const absoluteSrc = src.startsWith('http') ? src : `${window.location.origin}${src}`;
      fetch(absoluteSrc)
        .then(r => r.blob())
        .then(blob => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror   = () => resolve(src); // fallback: keep original
          reader.readAsDataURL(blob);
        })
        .catch(() => resolve(src)); // network error → keep original
    });

  /** Replace all url('/path/...') references inside a CSS string with data URIs.
   *  This handles background-image, mask-image, etc. that live in Emotion
   *  <style> blocks rather than in <img> src attributes. */
  const inlineStyleUrls = async (css: string): Promise<string> => {
    // Match url("..."), url('...'), url(...) — capture the inner path
    const urlPattern = /url\((['"]?)([^)'"]+)\1\)/g;
    const matches: { full: string; path: string }[] = [];
    let m: RegExpExecArray | null;
    while ((m = urlPattern.exec(css)) !== null) {
      const path = m[2];
      // Only inline relative or same-origin paths; skip data: and external https
      if (!path.startsWith('data:') && !path.startsWith('https://fonts.gstatic') && !path.startsWith('//')) {
        matches.push({ full: m[0], path });
      }
    }
    // Deduplicate by path so we only fetch each asset once
    const unique = [...new Map(matches.map(x => [x.path, x])).values()];
    const conversions = await Promise.all(unique.map(async ({ path, full }) => {
      const uri = await toDataUri(path);
      return { full, uri };
    }));
    let result = css;
    for (const { full, uri } of conversions) {
      // Replace every occurrence; escape special regex chars in the url() token
      result = result.split(full).join(`url("${uri}")`);
    }
    return result;
  };

  /** Export the live rendered DOM as a self-contained HTML file.
   *  All Emotion/MUI styles are already injected as <style> tags in the
   *  document — we capture them so the downloaded file renders identically
   *  to what the user sees in the previewer. No re-render, no print pipeline.
   *  All <img> srcs are converted to base64 data URIs so the file is fully
   *  portable — works offline and from file:// without any server. */
  const handleDownloadHTML = async () => {
    const el = reportRef.current;
    if (!el) return;
    setIsExportingHTML(true);
    try {

    // Clone so we can mutate img srcs without touching the live preview
    const clone = el.cloneNode(true) as HTMLElement;

    // Remove admin-only content that must never appear in exported reports
    clone.querySelectorAll('.no-export').forEach(el => el.remove());

    // Convert every image to a base64 data URI
    const imgs = Array.from(clone.querySelectorAll('img'));
    await Promise.all(imgs.map(async img => {
      const src = img.getAttribute('src');
      if (!src) return;
      img.setAttribute('src', await toDataUri(src));
    }));

    // Collect every <style> tag Emotion/MUI injected into the live document.
    // IMPORTANT: In production builds, Emotion uses CSSStyleSheet.insertRule() (speedy mode)
    // which leaves style.textContent EMPTY even though the styles are applied to the DOM.
    // We must read from sheet.cssRules to get the actual rules in production.
    // In dev mode, textContent is populated normally so both paths are needed.
    const inlineStyleParts: string[] = Array.from(document.querySelectorAll('style'))
      .map(s => {
        if (s.textContent?.trim()) return s.textContent;
        // Production Emotion: textContent is empty, rules are in sheet.cssRules
        try {
          return Array.from(s.sheet?.cssRules ?? [])
            .map(r => r.cssText)
            .join('\n');
        } catch { return ''; }
      });

    // Also fetch all <link rel="stylesheet"> hrefs (CRA prod bundle, Google Fonts, etc.)
    const linkHrefs = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map(l => (l as HTMLLinkElement).href)
      .filter(Boolean);
    const fetchedSheets = await Promise.all(
      linkHrefs.map(href =>
        fetch(href)
          .then(r => r.text())
          .catch(() => '') // skip if CORS-blocked (e.g. Google Fonts)
      )
    );
    fetchedSheets.forEach(css => { if (css) inlineStyleParts.push(css); });

    const rawStyles = inlineStyleParts.join('\n');
    const inlinedStyles = await inlineStyleUrls(rawStyles);

    const name = reportData?.birthDetails?.name ?? 'Report';
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${name} \u2013 ShreemAstro Vedic Report</title>
  <style>${inlinedStyles}</style>
  <style>
    /* Outer page chrome */
    html { background: #F3F4F6; margin: 0; padding: 0; }
    body { margin: 0; padding: 32px 0; background: #F3F4F6; }
    /* Remove the A4-page min-height that each section carries for the print
       previewer. In HTML view the document should flow as natural content,
       not as fixed 297mm page-height blocks with large empty gaps. */
    .report-section { min-height: unset !important; }
    /* Print settings when the user does Ctrl+P from the HTML file */
    @media print {
      html, body { background: #fff; padding: 0; margin: 0; }
      @page { size: A4 portrait; margin: 17mm 10mm 8mm; }
      @page :first { margin: 0; }
    }
  </style>
</head>
<body>
  ${clone.outerHTML}
  <script>
    // Show Aspects toggle — scoped per chart so D1 and D9 are independent
    document.querySelectorAll('input[data-aspects-toggle]').forEach(function(cb) {
      cb.addEventListener('change', function() {
        var scope = cb.getAttribute('data-aspects-toggle');
        document.querySelectorAll('[data-aspects-item="' + scope + '"]').forEach(function(el) {
          el.style.display = cb.checked ? '' : 'none';
        });
      });
    });
    // Smooth scroll for all internal anchor links (↑ Index, section nav)
    document.querySelectorAll('a[href^="#"]').forEach(function(a) {
      a.addEventListener('click', function(e) {
        var id = a.getAttribute('href').slice(1);
        var target = document.getElementById(id);
        if (target) { e.preventDefault(); target.scrollIntoView({ behavior: 'smooth' }); }
      });
    });
  </script>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name.replace(/\s+/g, '_')}_ShreemAstro_VedicReport.html`;
    a.click();
    URL.revokeObjectURL(url);
    } finally {
      setIsExportingHTML(false);
    }
  };
  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: reportData
      ? `ShreemAstro_Report_${reportData.birthDetails.name.replace(/\s+/g, '_')}`
      : 'ShreemAstro_Report',
    pageStyle: `
      /* ── Page layout ─────────────────────────────────────── */
      @page {
        size: A4 portrait;
        /* Top margin = header height (12mm) + 5mm visible breathing room */
        margin-top: 17mm;
        /* Bottom: 8mm is enough without a running footer */
        margin-bottom: 8mm;
        margin-left: 10mm;
        margin-right: 10mm;
      }
      /* Cover page: full-bleed, no margin */
      @page :first {
        margin: 0 !important;
      }

      /* ── Admin-only content: hidden in all exports ─────── */
      .no-export { display: none !important; }

      /* ── Colour accuracy ─────────────────────────────────── */
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
      }
      body { background: #ffffff !important; }

      /* ── Running page header (position:fixed repeats every page) */
      .print-page-header {
        display: flex !important;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: 12mm;
        align-items: center;
        justify-content: space-between;
        padding: 0 8mm;
        background: #ffffff;
        border-bottom: 1.5pt solid #F59E0B;
        font-size: 7pt;
        color: #6B7280;
        z-index: 9999;
      }
      .print-page-header-brand {
        font-weight: 700;
        color: #5B21B6;
        letter-spacing: 0.5pt;
      }
      .print-page-header-name {
        color: #374151;
        font-weight: 500;
      }
      .print-page-header-site {
        color: #9CA3AF;
      }

      /* ── Running page footer — DISABLED
         position:fixed bottom:0 misrenders in Chrome's print pipeline;
         it can appear at the top of subsequent pages instead of the bottom.
         Footer info is already covered by the header (shreemastro.com) and cover. */
      .print-page-footer {
        display: none !important;
      }

      /* ── Cover page: stack above header/footer on page 1 ─── */
      /* The cover has z-index:99999 which exceeds the header/footer
         z-index:9999, so the cover's background image paints over
         them on page 1. On pages 2+ there is no cover element,
         so header/footer render normally. */
      .report-cover {
        position: relative;
        z-index: 99999 !important;
      }

      /* ── Card / panel integrity ──────────────────────────── */
      /* Cards must never be sliced across a page boundary */
      .MuiCard-root {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }
      /* Grid cells: each cell keeps its card whole */
      .MuiGrid-item {
        break-inside: avoid !important;
        page-break-inside: avoid !important;
      }
      /* Table rows stay together */
      tr {
        break-inside: avoid;
        page-break-inside: avoid;
      }
      /* Section headers must never be orphaned from content below */
      .report-section-header {
        break-after: avoid !important;
        page-break-after: avoid !important;
      }
      /* Each named section starts on a fresh page */
      .report-section {
        break-before: page;
        page-break-before: always;
      }
      /* First content section (birth details) immediately follows cover —
         it already gets a forced break from the cover's pageBreakAfter,
         so we rely on that and don't add extra space */
      .report-section:first-of-type {
        break-before: auto;
        page-break-before: auto;
      }

      /* ── Print-safe system fonts ────────────────────────────────────── */
      /* OS system-UI font stack: renders crisp without any network request. */
      /* NOTE: 'span' and 'div' are intentionally excluded — MUI uses span   */
      /* for icon glyphs and emoji; forcing Arial on them breaks icon output. */
      :root {
        --joy-fontFamily-body: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, Helvetica, sans-serif;
        --joy-fontFamily-display: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, Helvetica, sans-serif;
        --joy-fontFamily-code: "Consolas", "Courier New", monospace;
      }
      body, p, td, th, li, label, button {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, Helvetica, sans-serif !important;
      }
      h1, h2, h3, h4, h5, h6 {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, Helvetica, sans-serif !important;
      }

      /* ── Top padding: cosmetic gap only ── */
      /* @page margin-top: 17mm already clears the 12mm header.            */
      /* This 3mm adds a subtle visual breath at the top of every section. */
      .report-section {
        padding-top: 3mm;
      }
    `,

  });

  // Null check - should never happen but defensive coding
  if (!reportData) {
    return null;
  }

  /** Renders AI-generated content as a formatted report section */
  const renderAISection = (title: string, content: string | undefined, loading: boolean | undefined, accentColor: string) => {
    if (!content && !loading) return null;
    return (
      <Box className="report-section" sx={{ mt: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: `2px solid ${accentColor}` }}>
          <Typography level="h3" sx={{ fontWeight: 700, color: accentColor, fontSize: '1.1rem' }}>
            {title}
          </Typography>
          {loading && <CircularProgress size="sm" sx={{ ml: 'auto' }} />}
        </Box>
        {loading && !content && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 3, color: 'text.secondary' }}>
            <CircularProgress size="sm" />
            <Typography level="body-sm">Generating AI analysis…</Typography>
          </Box>
        )}
        {content && (
          <Box sx={{
            fontSize: '0.85rem',
            lineHeight: 1.7,
            color: 'text.primary',
            '& p': { mb: 1 },
            '& strong': { fontWeight: 700 },
            whiteSpace: 'pre-wrap',
          }}>
            {/* Render markdown-light: bold **text**, headers ## and ### */}
            {content.split('\n').map((line, idx) => {
              if (line.startsWith('### ')) return <Typography key={idx} level="title-sm" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5, color: accentColor }}>{line.slice(4)}</Typography>;
              if (line.startsWith('## ')) return <Typography key={idx} level="title-md" sx={{ fontWeight: 700, mt: 2, mb: 0.5, color: accentColor }}>{line.slice(3)}</Typography>;
              if (line.startsWith('**') && line.endsWith('**') && line.length > 4) return <Typography key={idx} level="body-sm" sx={{ fontWeight: 700, mt: 1 }}>{line.slice(2, -2)}</Typography>;
              if (line.trim() === '') return <Box key={idx} sx={{ height: '0.5em' }} />;
              // Inline bold: replace **text** with bold spans
              const parts = line.split(/(\*\*[^*]+\*\*)/g);
              return (
                <Typography key={idx} level="body-sm" sx={{ mb: 0.3 }}>
                  {parts.map((part, pi) =>
                    part.startsWith('**') && part.endsWith('**')
                      ? <strong key={pi}>{part.slice(2, -2)}</strong>
                      : part
                  )}
                </Typography>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  // Default configuration
  const config: ReportConfig = {
    type: 'comprehensive',
    includeSections: [
      'birth-details',
      'chart-d1',
      'planet-details',
      'planet-factors',
      'house-scores',
      'd1-house-scores',
      'life-areas',
      'yogas',
      'dasha-timeline',
      'year-forecast',
      'opportunity-windows',
    ],
    includeAIAnalysis: false,
    title: 'Comprehensive Vedic Report',
    ...customConfig,
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 10004 }}>
      <ModalDialog
        size="lg"
        sx={{
          maxWidth: fullScreen ? '100vw' : '95vw',
          maxHeight: fullScreen ? '100vh' : '95vh',
          width: fullScreen ? '100vw' : '1200px',
          height: fullScreen ? '100vh' : undefined,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: fullScreen ? 0 : undefined,
          p: fullScreen ? 0 : undefined,
        }}
      >
        {/* Header — hidden in fullscreen */}
        {!fullScreen && (
          <>
            <ModalClose />
            <Box sx={{ mb: 2 }}>
              <Typography level="h3" sx={{ mb: 1 }}>
                Report Preview
              </Typography>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Preview your comprehensive astrology report before downloading
              </Typography>
            </Box>
          </>
        )}

        {/* Action buttons */}
        <Box sx={{
          display: 'flex', gap: 1, mb: fullScreen ? 0 : 2, alignItems: 'center',
          ...(fullScreen && {
            position: 'fixed', top: 16, right: 16, zIndex: 10010,
          }),
        }}>
          {/* Left-aligned AI analysis buttons (admin only, not shown in fullscreen) */}
          {isAdmin && !fullScreen && (
            <Box sx={{ display: 'flex', gap: 1, mr: 'auto', alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                size="sm"
                variant={swotContent ? 'outlined' : 'soft'}
                color="primary"
                onClick={onAddSwotAnalysis}
                disabled={swotLoading}
                startDecorator={swotLoading ? <CircularProgress size="sm" /> : (swotContent ? '↺' : '🧠')}
                sx={{ fontSize: '0.78rem' }}
              >
                {swotLoading ? 'Generating…' : swotContent ? 'Regenerate SWOT' : 'Add SWOT Analysis'}
              </Button>
              <Button
                size="sm"
                variant={detailedContent ? 'outlined' : 'soft'}
                color="success"
                onClick={onAddDetailedAnalysis}
                disabled={detailedLoading}
                startDecorator={detailedLoading ? <CircularProgress size="sm" /> : (detailedContent ? '↺' : '📋')}
                sx={{ fontSize: '0.78rem' }}
              >
                {detailedLoading ? 'Generating…' : detailedContent ? 'Regenerate Detailed' : 'Add Detailed Chart Analysis'}
              </Button>
              <Button
                size="sm"
                variant={currentPeriodContent ? 'outlined' : 'soft'}
                color="warning"
                onClick={onAddCurrentPeriodAnalysis}
                disabled={currentPeriodLoading}
                startDecorator={currentPeriodLoading ? <CircularProgress size="sm" /> : (currentPeriodContent ? '↺' : '⏳')}
                sx={{ fontSize: '0.78rem' }}
              >
                {currentPeriodLoading ? 'Generating…' : currentPeriodContent ? 'Regenerate Current Period' : 'Add Current Period Analysis'}
              </Button>
              <Button
                size="sm"
                variant={yearForecastContent ? 'outlined' : 'soft'}
                color="neutral"
                onClick={onAddYearForecast}
                disabled={yearForecastLoading}
                startDecorator={yearForecastLoading ? <CircularProgress size="sm" /> : (yearForecastContent ? '↺' : '📅')}
                sx={{ fontSize: '0.78rem' }}
              >
                {yearForecastLoading ? 'Generating…' : yearForecastContent ? 'Regenerate Monthly Predictions' : 'Add Monthly Predictions'}
              </Button>
              {/* Clear Cache button — only shown when there is something cached */}
              {reportAiCacheKey && (swotContent || detailedContent || currentPeriodContent || yearForecastContent) && (
                <Button
                  size="sm"
                  variant="plain"
                  color="danger"
                  onClick={handleClearAiCache}
                  sx={{ fontSize: '0.75rem', opacity: 0.7, '&:hover': { opacity: 1 } }}
                >
                  {cacheCleared ? '✓ Cache Cleared' : '🗑 Clear AI Cache'}
                </Button>
              )}
            </Box>
          )}

          {/* Right-aligned standard buttons */}
          {!fullScreen && (
            <Button
              onClick={handlePrint}
              startDecorator={isGenerating ? <CircularProgress size="sm" /> : '📄'}
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating PDF...' : 'Download PDF'}
            </Button>
          )}
          {!fullScreen && (
            <Button
              variant="outlined"
              color="success"
              onClick={handleDownloadHTML}
              disabled={isExportingHTML}
              startDecorator={isExportingHTML ? <CircularProgress size="sm" /> : '🌐'}
            >
              {isExportingHTML ? 'Preparing…' : 'Export HTML'}
            </Button>
          )}
          {!fullScreen && (
            <Button variant="outlined" onClick={onClose}>
              Close Preview
            </Button>
          )}
          <Button
            variant={fullScreen ? 'solid' : 'outlined'}
            color={fullScreen ? 'neutral' : 'primary'}
            onClick={() => setFullScreen(f => !f)}
            startDecorator={fullScreen ? '✕' : '⛶'}
            size={fullScreen ? 'sm' : 'md'}
            sx={fullScreen ? { opacity: 0.85, '&:hover': { opacity: 1 } } : {}}
          >
            {fullScreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </Button>
        </Box>

        {/* Report preview (scrollable) */}
        <Sheet
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            border: fullScreen ? 'none' : '1px solid',
            borderColor: 'divider',
            borderRadius: fullScreen ? 0 : 'sm',
            p: fullScreen ? 0 : 3,
            background: '#FFF9F5',
          }}
        >
          <Box ref={reportRef} sx={{
            maxWidth: '210mm', mx: 'auto', background: '#FFF', minHeight: 'fit-content',
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
            '@media print': {
              // In print the @page margins already constrain the page area;
              // setting maxWidth: 100% prevents the 210mm box from
              // overflowing the narrower @page content area.
              maxWidth: '100%',
              mx: 0,
            },
          }}>
            {/* Running page header — hidden on screen, fixed on every printed page */}
            <Box
              className="print-page-header"
              sx={{
                display: 'none',
                '@media print': {
                  display: 'flex',
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '12mm',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: '8mm',
                  background: '#fff',
                  borderBottom: '1.5pt solid #F59E0B',
                  zIndex: 9999,
                },
              }}
            >
              <span style={{ color: '#5B21B6', fontWeight: 700, fontSize: '8pt' }}>🕉️ ShreemAstro<sup style={{ fontSize: '0.6em', fontWeight: 400, verticalAlign: 'super', lineHeight: 0 }}>™</sup></span>
              <span style={{ color: '#374151', fontSize: '7.5pt' }}>{reportData.birthDetails.name} · Comprehensive Vedic Report</span>
              <span style={{ color: '#9CA3AF', fontSize: '7pt' }}>shreemastro.com</span>
            </Box>

            {/* Running page footer — disabled: position:fixed bottom:0 misrenders in Chrome print */}
            <Box className="print-page-footer" sx={{ display: 'none' }} />

            {/* Cover page */}
            <ReportCover
              data={reportData}
              title={config.title}
              subtitle={config.subtitle}
            />

            {/* Intro page — philosophy, JP Morgan quote, Karma/Yogananda quotes */}
            <ReportIntroPage data={reportData} />

            {/* Index page — report strengths, how to use, section TOC */}
            <Box id="report-index">
              <ReportIndexPage
                data={reportData}
                isAdmin={isAdmin}
                hasSwot={!!(swotContent)}
                hasDetailed={!!(detailedContent)}
                hasCurrentPeriod={!!(currentPeriodContent)}
                onSectionClick={handleSectionClick}
              />
            </Box>

            {/* Content sections - COMPACT LAYOUT for space efficiency */}
            <Box
              sx={{
                p: 2, // Reduced from 4 (16px instead of 32px)
                '& > *': {
                  mb: 2, // Reduced from 4 (20px vertical spacing between sections)
                },
                '& .MuiCard-root': {
                  mb: 1.5, // Reduced from 2 (12px vertical spacing between cards)
                },
                '& .MuiGrid-root': {
                  gap: 1.5, // Reduced from 2 (12px gap in grids)
                },
              }}
            >
              {/* Header for content pages */}
              <Box
                className="page-header no-print-first-page"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pb: 1, // Reduced from 2
                  mb: 2, // Reduced from 4
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '@media print': {
                    display: 'none',
                    '.page-break + &': {
                      display: 'flex',
                    }
                  }
                }}
              >
                <Typography level="body-sm" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  🕉️ ShreemAstro<Box component="sup" sx={{ fontSize: '0.6em', fontWeight: 400, verticalAlign: 'super', lineHeight: 0, color: 'rgba(91,33,182,1)' }}>™</Box>
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  {reportData.birthDetails.name}
                </Typography>
              </Box>

              {/* Sections in logical order */}
              
              {/* 1. Birth Details top: personal info + chart essentials */}
              {config.includeSections.includes('birth-details') && (
                <Box id="section-birth-details">
                  <BirthDetailsSection data={reportData} config={config} phase="top" onNavigate={onNavigate ? (tab) => { onClose(); onNavigate(tab); } : undefined} />
                </Box>
              )}

              {/* 2. Chart D1 - visual representation */}
              {config.includeSections.includes('chart-d1') && (
                <Box id="section-chart-d1">
                  <ChartSection data={reportData} config={config} astroData={astroData} vargaMap={vargaMap} onNavigate={onNavigate ? (tab) => { onClose(); onNavigate(tab); } : undefined} />
                </Box>
              )}

              {/* 3. Planet Details - complete planetary table */}
              {config.includeSections.includes('planet-details') && (
                <Box id="section-planet-details">
                  <PlanetDetailsSection data={reportData} config={config} onNavigate={onNavigate ? (tab) => { onClose(); onNavigate(tab); } : undefined} />
                </Box>
              )}

              {/* 3.5 Planet Factors — Shadbala, Ishta-Kashta, Blended Strength */}
              {config.includeSections.includes('planet-factors') && (
                <Box id="section-planet-factors">
                  <PlanetFactorsSection data={reportData} config={config} onNavigate={onNavigate ? (tab) => { onClose(); onNavigate(tab); } : undefined} />
                </Box>
              )}

              {/* 4. House-by-House Analysis: zodiac + placement + 4 metric bars (merged panel) */}
              {config.includeSections.includes('house-scores') && (
                <Box id="section-house-scores">
                  <HouseCombinedSection data={reportData} config={config} onNavigate={onNavigate ? (tab) => { onClose(); onNavigate(tab); } : undefined} />
                </Box>
              )}

              {/* AI: SWOT Analysis (admin only — after house-scores) */}
              <Box id="section-swot">
                {isAdmin && renderAISection('SWOT Analysis', swotContent, swotLoading, '#7C3AED')}
              </Box>

              {/* 6.5 D1 House Scores — Activities to Balance Houses */}
              {config.includeSections.includes('d1-house-scores') && (
                <Box id="section-d1-house-scores">
                  <HouseBalanceSection data={reportData} config={config} onNavigate={onNavigate ? (tab) => { onClose(); onNavigate(tab); } : undefined} />
                </Box>
              )}

              {/* 7. Life Areas - 12 life domains dashboard */}
              {config.includeSections.includes('life-areas') && (
                <Box id="section-life-areas">
                  <LifeAreasSection data={reportData} config={config} onNavigate={onNavigate ? (tab) => { onClose(); onNavigate(tab); } : undefined} />
                </Box>
              )}

              {/* AI: Detailed Chart Analysis (admin only — after Life Areas) */}
              <Box id="section-detailed">
                {isAdmin && renderAISection('Detailed Chart Analysis', detailedContent, detailedLoading, '#047857')}
              </Box>

              {/* 8. Yogas - classical yoga combinations */}
              {config.includeSections.includes('yogas') && (
                <Box id="section-yogas">
                  <YogasSection data={reportData} config={config} onNavigate={onNavigate ? (tab) => { onClose(); onNavigate(tab); } : undefined} />
                </Box>
              )}

              {/* 9. Dasha Timeline - current planetary periods */}
              {config.includeSections.includes('dasha-timeline') && (
                <Box id="section-dasha-timeline">
                  <DashaTimelineSection data={reportData} config={config} onNavigate={onNavigate ? (tab) => { onClose(); onNavigate(tab); } : undefined} />
                </Box>
              )}

              {/* AI: Current Period Analysis (admin only — after dasha timeline) */}
              <Box id="section-current-period">
                {isAdmin && renderAISection('Current Period Analysis', currentPeriodContent, currentPeriodLoading, '#B45309')}
              </Box>

              {/* 10. Year at a Glance — Life Areas Forecast (swimlane chart) */}
              {config.includeSections.includes('year-forecast') && (
                <Box id="section-year-forecast">
                  <YearForecastSection data={reportData} config={config} onNavigate={onNavigate ? (tab) => { onClose(); onNavigate(tab); } : undefined} />
                </Box>
              )}

              {/* AI: Year-ahead Monthly Predictions (admin only — after swimlane chart) */}
              <Box id="section-year-forecast-ai">
                {isAdmin && renderAISection('Year-ahead Monthly Predictions', yearForecastContent, yearForecastLoading, '#0369A1')}
              </Box>

              {/* 11. Opportunity & Caution Windows - feed-based prediction windows */}
              {config.includeSections.includes('opportunity-windows') && (
                <Box id="section-opportunity-windows">
                  <OpportunityWindowsSection data={reportData} config={config} onNavigate={onNavigate ? (tab) => { onClose(); onNavigate(tab); } : undefined} />
                </Box>
              )}

              {/* FAQ page — 8 real-life questions + ShreemAstro analysis callout + CTA */}
              <ReportFAQPage data={reportData} />

              {/* Closing page — app promo + registration + Bhagavad Gita quote + signature */}
              <ReportClosingPage data={reportData} />

              {/* Footer for content pages */}
              <Box
                className="page-footer"
                sx={{
                  mt: 6,
                  pt: 3,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  ShreemAstro<Box component="sup" sx={{ fontSize: '0.6em', fontWeight: 400, verticalAlign: 'super', lineHeight: 0, color: 'rgba(91,33,182,0.8)' }}>™</Box> Vedic Report
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  www.shreemastro.com
                </Typography>
              </Box>
            </Box>
          </Box>
        </Sheet>
      </ModalDialog>
    </Modal>
  );
};
